// Part of @roc/feature-track-record-v2 — isolated copy of the redesigned Track
// Record UI used only by crm-portal and roc-internal-portal. The original lives
// in @roc/feature-borrowers and is left untouched so other portals keep the old
// design. MobX stores/services remain shared (imported from @roc/feature-borrowers).

import React, { useEffect, useState } from 'react';
import {
  Checkbox,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TablePagination,
  TableHead,
  MenuItem,
  Menu,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { Delete, Edit, Warning, Check, Cancel, Flag } from '@material-ui/icons';
import { ConfirmDialog, DialogState } from '@roc/ui';

const useTableStyles = makeStyles((theme) => {
  return {
    trackRecordGrid: (props: any) => ({
      boxShadow: 'none',
      border: '1px solid lightgrey',
      backgroundColor: 'transparent',
      '& tbody': {
        // border: 'solid lightgrey 1px',
        padding: '12px',
      },
      '& td': {
        borderBottom: '1px solid lightgrey',
        padding: '12px 0px 12px 12px',
      },
      ...(props.isExpandable && {
        '& tr:nth-child(even)': {
          borderBottom: 'solid lightgrey 1px',
        },
      }),
      ...(!props.isExpandable && {
        '& tr': {
          borderBottom: 'solid lightgrey 1px',
        },
      }),
    }),
    tableCellActions: {
      textAlign: 'center',
      padding: '12px 16px',
      // borderRight: '1px solid lightgray',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: '150px',
    },
    tableCellLeftAlign: {
      textAlign: 'left',
      padding: '12px 16px',
      borderRight: '1px solid lightgray',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: '150px',
      backgroundColor: '#EEEEEE'
    },
    tableCell: {
      textAlign: 'center',
      padding: '12px 16px',
      borderRight: '1px solid lightgray',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: '150px',
      backgroundColor: '#EEEEEE'
    },
  };
});

interface TrackRecordTableProps {
  store: any;
  isExpandable?: boolean;
  headers: any;
  rows: any;
  rowsPerPageOptions?: number[];
  defaultRowsPerPage?: number;
  enablePagination?: boolean;
  borrowerBaseballCardStore?: any;
  selectable?: boolean;
  selectedIds?: any[];
  onToggleRow?: (row: any) => void;
  onToggleAllRows?: (rows: any[], checked: boolean) => void;
}

export const TrackRecordTable = observer(({
  store,
  isExpandable = false,
  headers,
  rows,
  rowsPerPageOptions = [5, 10, 15],
  defaultRowsPerPage = 5,
  enablePagination = false,
  borrowerBaseballCardStore,
  selectable = false,
  selectedIds = [],
  onToggleRow = null,
  onToggleAllRows = null,
}: TrackRecordTableProps) => {
  const classes = useTableStyles({ isExpandable });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    enablePagination ? defaultRowsPerPage : rows.length
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [propertyPendingRemoval, setPropertyPendingRemoval] = useState(null);
  const [entityPendingRemoval, setEntityPendingRemoval] = useState(null);
  // Inline doc-row expansion (like master). Local state, separate from
  // store.currentExpandedRowIndex (used by the edit-pencil/modal flow).
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  useEffect(() => {
    setRowsPerPage(
      enablePagination ? defaultRowsPerPage : rows.length
    );
  }, [enablePagination])

  // Toggle the inline documents row: load the row's tasks into currentTasks
  // without opening the edit modal (unlike handleExpandClick below).
  const handleToggleExpand = async (id, i, type, object) => {
    if (expandedIndex === i) {
      setExpandedIndex(null);
      store.currentTasks = [];
      return;
    }
    store.currentTasks = [];
    store.currentProperty = object;
    const taskBody = type === 'entity'
      ? {
        sectionProcess: 'TRACK_RECORD_REVIEW',
        objectType: 'BORROWER_ENTITY_BORROWER',
        taskTemplateType: 'BORROWER_ENTITY_BORROWER',
        objectId: id,
      }
      : {
        sectionProcess: 'TRACK_RECORD_REVIEW',
        objectType: 'PROPERTY_TRACK_RECORD',
        taskTemplateType: 'PROPERTY_TRACK_RECORD',
        objectId: id,
      };
    await store.getTasks(taskBody);
    setExpandedIndex(i);
  };

  const handleExpandClick = async (id, i, type, object) => {
    if (borrowerBaseballCardStore) {
      borrowerBaseballCardStore.propertyInformationDialogState = DialogState.EDIT;
    }
    store.trackRecordPropertyFormStore.form.fields.screenType.value = 'addProperty';
    store.currentTasks = [];
    if (store.currentExpandedRowIndex === i) {
      store.setCurrentExpandedRowIndex(null);
      store.currentProperty = null;
    } else {
      store.setCurrentExpandedRowIndex(i);
      store.currentProperty = object;
      const taskEntityBody = {
        sectionProcess: 'TRACK_RECORD_REVIEW',
        objectType: 'BORROWER_ENTITY_BORROWER',
        taskTemplateType: 'BORROWER_ENTITY_BORROWER',
        objectId: id,
      };

      const taskPropertyBody = {
        sectionProcess: 'TRACK_RECORD_REVIEW',
        objectType: 'PROPERTY_TRACK_RECORD',
        taskTemplateType: 'PROPERTY_TRACK_RECORD',
        objectId: id,
      };

      const taskBody = type === 'entity' ? taskEntityBody : taskPropertyBody;
      await store.getTasks(taskBody);
      if (type === 'entity') {
        store.handleEditExistingTrackRecordEntity(object, i);
      } else {
        store.handleEditExistingTrackRecordProperty(object, i);
      }
    }
  };

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setTimeout(() => setSelectedRow(null), 200);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    store.setCurrentExpandedRowIndex(null);
    setExpandedIndex(null);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    store.setCurrentExpandedRowIndex(null);
    setExpandedIndex(null);
  };

  const displayedRows = enablePagination
    ? rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage)
    : rows;

  return (
    <div>
      <ConfirmDialog
        open={!!propertyPendingRemoval}
        title="Remove Track Record"
        confirmButtonText="Yes"
        body={
          <>
            <Warning color="error" fontSize="large" style={{ marginRight: '16px' }} />
            <span>Are you sure you want to remove the record?</span>
          </>
        }
        handleClose={() => setPropertyPendingRemoval(null)}
        handleCancel={() => setPropertyPendingRemoval(null)}
        handleConfirm={() => {
          if (propertyPendingRemoval) {
            store.handleRemoveTrackRecordProperty(propertyPendingRemoval);
          }
          setPropertyPendingRemoval(null);
        }}
      />
      <ConfirmDialog
        open={!!entityPendingRemoval}
        title="Confirmation"
        body="Are you sure you want to remove this entity?"
        handleClose={() => setEntityPendingRemoval(null)}
        handleCancel={() => setEntityPendingRemoval(null)}
        handleConfirm={() => {
          // Use the row-provided onRemove (authenticated baseball-card path)
          // rather than the verification store's public endpoint.
          entityPendingRemoval?.onRemove?.();
          setEntityPendingRemoval(null);
        }}
      />
        <TableContainer className={classes.trackRecordGrid}>
          <Table>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell className={classes.tableCell} style={{ padding: '3px', width: '48px' }}>
                  <Checkbox
                    size="small"
                    checked={displayedRows.length > 0 && displayedRows.every((r) => selectedIds.includes(r.id))}
                    indeterminate={displayedRows.some((r) => selectedIds.includes(r.id)) && !displayedRows.every((r) => selectedIds.includes(r.id))}
                    onChange={(e) => onToggleAllRows?.(displayedRows, e.target.checked)}
                  />
                </TableCell>
              )}
              {isExpandable && (
                <TableCell className={classes.tableCell} style={{ padding: '3px', width: '48px' }} />
              )}
              {headers.map((header, headerIndex) => (
                <TableCell
                  key={header.name}
                  className={headerIndex === 1 && headers.length <= 3 ? classes.tableCellLeftAlign : classes.tableCell}
                  width={header.width}
                >
                  {header.name}
                </TableCell>
              ))}
              {isExpandable && (
                <TableCell className={classes.tableCell}>Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
            <TableBody>
              {displayedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={headers.length + (isExpandable ? 2 : 0) + (selectable ? 1 : 0)} align="center">
                    No records found
                  </TableCell>
                </TableRow>
              )
                : (
                  displayedRows.map((row, index) => (
                    <React.Fragment key={index}>
                      <TableRow>
                        {selectable && (
                          <TableCell style={{ padding: '3px', width: '48px' }}>
                            <Checkbox
                              size="small"
                              checked={selectedIds.includes(row.id)}
                              onChange={() => onToggleRow?.(row)}
                            />
                          </TableCell>
                        )}
                        {isExpandable && row.expandableContent && (
                          <TableCell style={{ padding: '3px', width: '48px' }}>
                            <IconButton
                              aria-label="expand row"
                              size="small"
                              onClick={async () => {
                                await handleToggleExpand(row.id, index, row.type, row.object);
                              }}
                            >
                              {expandedIndex === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                        )}
                        {row.cells.map((cell, cellIndex) => (
                          <TableCell key={cellIndex} width={headers[cellIndex].width}>{cell}</TableCell>
                        ))}
                        {isExpandable && row.expandableContent && (
                          <TableCell className={classes.tableCellActions} width='9%'>
                            {/* Unified actions menu for properties and entities.
                                Items are gated by per-row props, so entities (no
                                canEdit) show Upload / Verify / Unverify / Remove
                                with no Edit, matching prod. */}
                            {!row?.object?.verificationInProgress && (
                              <IconButton
                                aria-label="actions"
                                size="small"
                                onClick={(e) => handleMenuOpen(e, row)}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            )}
                            <Menu
                              anchorEl={anchorEl}
                              keepMounted
                              open={Boolean(anchorEl) && selectedRow?.id === row.id}
                              onClose={handleMenuClose}
                            >
                              {row.canEdit && (
                                <MenuItem
                                  onClick={async () => {
                                    await handleExpandClick(row.id, index, row.type, row.object);
                                    handleMenuClose();
                                  }}
                                >
                                  <Edit fontSize="small" style={{ marginRight: 8 }} />
                                  Edit
                                </MenuItem>
                              )}
                              {row.onUploadDocument && (
                                <MenuItem
                                  onClick={() => {
                                    row.onUploadDocument();
                                    handleMenuClose();
                                  }}
                                >
                                  <CloudUploadIcon fontSize="small" style={{ marginRight: 8 }} />
                                  Upload Document
                                </MenuItem>
                              )}
                              {row.canVerify && (
                                <MenuItem
                                  onClick={() => {
                                    row.onVerify?.();
                                    handleMenuClose();
                                  }}
                                >
                                  <Check fontSize="small" style={{ marginRight: 8 }} />
                                  Verify {row.type === 'entity' ? 'Entity' : 'Property'}
                                </MenuItem>
                              )}
                              {row.canUnverify && (
                                <MenuItem
                                  onClick={() => {
                                    row.onUnverify?.();
                                    handleMenuClose();
                                  }}
                                >
                                  <Cancel fontSize="small" style={{ marginRight: 8 }} />
                                  Unverify {row.type === 'entity' ? 'Entity' : 'Property'}
                                </MenuItem>
                              )}
                              {row.canCreateResolution && (
                                <MenuItem
                                  onClick={() => {
                                    row.onCreateResolution?.();
                                    handleMenuClose();
                                  }}
                                >
                                  <Flag fontSize="small" style={{ marginRight: 8 }} />
                                  Create Resolution
                                </MenuItem>
                              )}
                              {row.canRemove && (
                                <MenuItem
                                  onClick={() => {
                                    if (row.type === 'entity') {
                                      setEntityPendingRemoval(row);
                                    } else {
                                      setPropertyPendingRemoval(row?.object);
                                    }
                                    handleMenuClose();
                                  }}
                                >
                                  <Delete fontSize="small" style={{ marginRight: 8 }} />
                                  Remove
                                </MenuItem>
                              )}
                            </Menu>
                          </TableCell>
                        )}
                      </TableRow>
                      {isExpandable && row.expandableContent && (
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={headers.length + 2 + (selectable ? 1 : 0)}>
                            <Collapse in={expandedIndex === index} timeout="auto" unmountOnExit>
                              <div style={{ padding: 16 }}>
                                {row.expandableContent}
                              </div>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
            </TableBody>
          </Table>

          {enablePagination && (
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
      </TableContainer>
    </div>
  );
});
